import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { BadPressMatchRecord } from 'app/entities/monitoring/actual/badpress-match.model';
import { BadPressService } from 'app/entities/monitoring/actual/badpress/bad-press.service';
import { PepMatchRecord } from 'app/entities/monitoring/actual/pep-match.model';
import { PoliticallyExposedService } from 'app/entities/monitoring/actual/politicallyexposed/politically-exposed.service';
import { SanctionEntity, SanctionMatchRecord } from 'app/entities/monitoring/actual/sanction-matches.model';
import { SanctionService } from 'app/entities/monitoring/actual/sanction/sanction.service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';

export type BulkMonitoringHitType = 'Sanction' | 'Pep' | 'Badpress';

export interface BulkMonitoringRow {
  type: BulkMonitoringHitType;
  title: string;
  relationEntityId?: number;
  raw: any;
}

@Component({
  templateUrl: './resolve-bulk-hits.component.html',
  styleUrls: ['./resolve-bulk-hits.component.css']
})
export class ResolveBulkHitsComponent implements OnInit {
  private _rows: BulkMonitoringRow[] = [];
  sanctionPepResolutions: ApplicationValue[];
  badPressResolutions: ApplicationValue[];

  get rows(): BulkMonitoringRow[] {
    return this._rows;
  }

  set rows(value: BulkMonitoringRow[]) {
    this._rows = value || [];
    this.refreshResolutionOptions();
  }

  resolveForm: FormGroup;
  matchResolutions: ApplicationValue[];
  selectedMatchResolution: string;
  falseMatchReasons: ApplicationValue[];
  submitting = false;

  constructor(
    private sanctionService: SanctionService,
    private politicallyExposedService: PoliticallyExposedService,
    private badPressService: BadPressService,
    private warmUpService: WarmUpService,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    public dialogRef: MdDialogRef<ResolveBulkHitsComponent>,
    private snackBarService: SnackBarService
  ) { }

  ngOnInit() {
    this.buildForm();
    this.setUpSanctionPepResolutions(
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.SANCTION_MATCH_RESOLUTION));
    this.setUpBadPressResolutions(
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.PANAMA_PAPERS_MATCH_RESOLUTION));
    this.setUpFalseMatchReasons(
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.FALSE_MATCH_REASONS));
    this.refreshResolutionOptions();
  }

  get hitCount(): number {
    return (this.rows && this.rows.length) || 0;
  }

  get summaryText(): string {
    if (!this.rows || !this.rows.length) {
      return '';
    }
    const sanction = this.rows.filter(r => r.type === 'Sanction').length;
    const pep = this.rows.filter(r => r.type === 'Pep').length;
    const badpress = this.rows.filter(r => r.type === 'Badpress').length;
    const parts: string[] = [];
    if (sanction) { parts.push(`${sanction} sanction${sanction === 1 ? '' : 's'}`); }
    if (pep) { parts.push(`${pep} PEP`); }
    if (badpress) { parts.push(`${badpress} adverse media`); }
    return parts.join(', ');
  }

  get hasMixedResolutionTypes(): boolean {
    if (!this.rows || !this.rows.length) {
      return false;
    }
    const hasBadPress = this.rows.some(r => r.type === 'Badpress');
    const hasSanctionOrPep = this.rows.some(r => r.type === 'Sanction' || r.type === 'Pep');
    return hasBadPress && hasSanctionOrPep;
  }

  buildForm() {
    this.resolveForm = this.formBuilder.group({
      matchResolution: ['', Validators.required],
      comments: [''],
      falseMatch: [''],
    });
  }

  setUpSanctionPepResolutions(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.sanctionPepResolutions = response.values;
        this.refreshResolutionOptions();
        this.cdr.detectChanges();
      });
    } else {
      this.sanctionPepResolutions = dropDownValues;
      this.refreshResolutionOptions();
    }
  }

  setUpBadPressResolutions(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.badPressResolutions = response.values;
        this.refreshResolutionOptions();
        this.cdr.detectChanges();
      });
    } else {
      this.badPressResolutions = dropDownValues;
      this.refreshResolutionOptions();
    }
  }

  setUpFalseMatchReasons(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.falseMatchReasons = response.values;
        this.cdr.detectChanges();
      });
    } else {
      this.falseMatchReasons = dropDownValues;
    }
  }

  private refreshResolutionOptions() {
    const onlyBadPress = this.rows &&
      this.rows.length > 0 &&
      this.rows.every(r => r.type === 'Badpress');
    this.matchResolutions = onlyBadPress ? this.badPressResolutions : this.sanctionPepResolutions;
  }

  selectMatchResolution(event) {
    this.selectedMatchResolution = event.value;
  }

  isSelectedMatchFalse(): boolean {
    const list = this.matchResolutions;
    if (!list || !this.selectedMatchResolution) {
      return false;
    }
    const falseMatch = list.find(matchResolution => matchResolution.name === 'False');
    return !!falseMatch && this.selectedMatchResolution === falseMatch.id.toString();
  }

  private resolveIdForType(type: BulkMonitoringHitType, resolutionName: string): number {
    const list = type === 'Badpress' ? this.badPressResolutions : this.sanctionPepResolutions;
    if (!list) {
      return null;
    }
    const match = list.find(v => v.name === resolutionName);
    return match ? match.id : null;
  }

  private buildResolveRequest(row: BulkMonitoringRow, value: any): Observable<any> {
    const selectedId = value.matchResolution;
    const selectedName = this.matchResolutions.find(v => v.id === selectedId || v.id.toString() === String(selectedId));
    const resolutionName = selectedName ? selectedName.name : null;
    const resolutionId = this.resolveIdForType(row.type, resolutionName);
    if (resolutionId == null) {
      return Observable.throw(new Error(`Resolution "${resolutionName}" is not available for ${row.type} hits.`));
    }

    if (row.type === 'Sanction') {
      const record = new SanctionMatchRecord();
      const sanctionEntity = new SanctionEntity();
      sanctionEntity.id = row.raw.sanctionEntity.id;
      record.sanctionEntity = sanctionEntity;
      record.relationEntityId = row.relationEntityId;
      record.matchResolution = resolutionId;
      record.falseMatchReason = value.falseMatch;
      record.comments = value.comments;
      return this.sanctionService.markSanctionResolve(record);
    }

    if (row.type === 'Pep') {
      const record = new PepMatchRecord();
      record.pepEntity = row.raw.pepEntity;
      record.relationEntityId = row.relationEntityId;
      record.matchResolution = resolutionId;
      record.falseMatchReason = value.falseMatch;
      record.comments = value.comments;
      return this.politicallyExposedService.markPepResolve(record);
    }

    const record = new BadPressMatchRecord();
    record.badPressEntity = row.raw.badPressEntity;
    record.relationEntityId = row.relationEntityId;
    record.matchResolution = resolutionId;
    record.falseMatchReason = value.falseMatch;
    record.comments = value.comments;
    return this.badPressService.markBadPressResolve(record);
  }

  onSubmit({ value, valid }) {
    if (!valid || !this.rows || !this.rows.length || this.submitting) {
      return;
    }

    this.submitting = true;
    const requests = this.rows.map(row =>
      this.buildResolveRequest(row, value).catch(() => Observable.of({ flagResult: false }))
    );

    Observable.forkJoin(requests).subscribe(
      responses => {
        this.submitting = false;
        const failed = (responses || []).filter(r => !r || !r.flagResult).length;
        if (failed === 0) {
          this.snackBarService.showSimpleMsj(`${this.rows.length} hit${this.rows.length === 1 ? '' : 's'} resolved.`);
          this.dialogRef.close({ resolvedCount: this.rows.length, rows: this.rows, formValue: value });
        } else {
          this.snackBarService.showSimpleMsj(
            `${this.rows.length - failed} resolved, ${failed} failed. Refresh the list and retry any remaining hits.`
          );
          this.dialogRef.close({ resolvedCount: this.rows.length - failed, partial: true, rows: this.rows, formValue: value });
        }
      },
      err => {
        this.submitting = false;
        this.snackBarService.showStatus(
          err,
          MessageService.SANCTION_RESOLVE_SUCCESS,
          MessageService.SANCTION_RESOLVE_FAILURE
        );
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/monitoring/dashboard/resolve-bulk-hits/resolve-bulk-hits.component.ts